import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import { useTournaments } from '../../hooks/useTournaments'
import { supabase } from '../../lib/supabase'
import { Card, CardHeader, Button, Input, Modal, Badge, DarkModeToggleCompact } from '../../components/ui'
import { Trophy, Users, Calendar, Plus, Trash2, Grid3x3, GitGraph, Layers, Sparkles, TrendingUp, Crown, Brain, Gamepad, UserCheck, ShieldAlert, Activity, Download, CheckCircle2, AlertCircle, Terminal, RefreshCw, Server } from 'lucide-react'
import { startDashboardTour, startWizardTour } from '../../lib/tour'
import { TournamentAdvisorModal } from '../../components/tools/TournamentAdvisorModal'
import { QRCodeGenerator } from '../../components/tools/QRCodeGenerator'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'

export const DashboardPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeTenant } = useTenant()
  const { user } = useAuth()
  const { tournaments, loading, createTournament, deleteTournament } = useTournaments()
  const [showModal, setShowModal] = useState(false)
  const [showAdvisor, setShowAdvisor] = useState(false)
  const [newTournament, setNewTournament] = useState({ name: '' })
  const [tournamentType, setTournamentType] = useState('classic')
  const [sportType, setSportType] = useState('goals')
  const [sportFormat, setSportFormat] = useState('teams') // 'teams' or 'individual'
  const initialFilter = searchParams.get('filter')
  const [filterType, setFilterType] = useState(
    initialFilter === 'esports' || initialFilter === 'sports' ? initialFilter : 'all'
  )

  useEffect(() => {
    const filterParam = searchParams.get('filter')
    if (filterParam === 'esports' || filterParam === 'sports') {
      setFilterType(filterParam)
    } else if (!filterParam) {
      setFilterType('all')
    }
  }, [searchParams])

  useEffect(() => {
    // Start tour als nog niet gezien
    const hasSeenTour = localStorage.getItem('propoule_tour_seen')
    if (!hasSeenTour) {
      // Wacht een seconde zodat UI kan laden
      const timer = setTimeout(() => {
        startDashboardTour()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    // Start Wizard Tour als modal opent (en nog niet gezien)
    if (showModal) {
      startWizardTour()
    }
  }, [showModal])

  useEffect(() => {
    // Check url voor externe triggers (bijv vanuit sidebar)
    if (searchParams.get('new') === 'true') {
      setShowModal(true)
      setSearchParams(params => {
        const newParams = new URLSearchParams(params)
        newParams.delete('new')
        return newParams
      })
    }
  }, [searchParams, setSearchParams])

  const handleCreate = async (e) => {
    e.preventDefault()

    // LIMIT CHECK: Free plan = max 1 tournament
    // LIMIT CHECK: Free plan = max 1 tournament
    if (activeTenant?.plan !== 'pro' && tournaments.length >= 1) {
      alert(`${t('dashboard.limit_reached_title')}\n\n${t('dashboard.limit_reached_body')}`)
      return
    }

    try {
      const newTourney = await createTournament({
        name: newTournament.name,
        type: tournamentType,
        status: 'active',
        sport_type: sportType,
        sport_format: sportFormat
      })

      if (tournamentType === 'poules') {
        // Auto-create "Algemeen" division
        const { data: div } = await supabase.from('divisions').insert({
          tournament_id: newTourney.id,
          tenant_id: activeTenant.id,
          name: t('divisions_page.division_general')
        }).select().single()

        // Auto-create default Poule A
        if (div) {
          await supabase.from('poules').insert({
            division_id: div.id,
            tenant_id: activeTenant.id,
            name: 'Poule A'
          })
        }

        navigate(`/admin/tournaments/${newTourney.id}/divisions`)
      } else if (tournamentType === 'knockout') {
        // Auto-create structure for Knock-out (container)
        const { data: div } = await supabase.from('divisions').insert({
          tournament_id: newTourney.id,
          tenant_id: activeTenant.id,
          name: t('sidebar.finals')
        }).select().single()

        if (div) {
          await supabase.from('poules').insert({
            division_id: div.id,
            tenant_id: activeTenant.id,
            name: t('bracket_page.title')
          })
        }

        // ALLWAYS go to the Tournament Dashboard for a consistent "landing" experience
        // UPDATE: User feedback suggests going directly to the Bracket is better for Knockout tournaments
        navigate(`/admin/tournaments/${newTourney.id}/bracket`)
      } else {
        navigate(`/admin/tournaments/${newTourney.id}/divisions`)
      }

      setShowModal(false)
      setNewTournament({ name: '' })
      setTournamentType('classic')
    } catch (err) {
      alert(t('common.save_error') + err.message)
    }
  }

  const handleDelete = async (tournament) => {
    if (confirm(t('dashboard.delete_confirm', { name: tournament.name }))) {
      try {
        await deleteTournament(tournament.id)
      } catch (err) {
        console.error('Delete error:', err)
        alert(`${t('dashboard.delete_error')}: ` + err.message)
      }
    }
  }

  // Realtime Database Statistieken State
  const [stats, setStats] = useState({
    teams: 0,
    matches: 0,
    players: 0,
    loading: true
  })

  // Gezondheid & Diagnose State
  const [diagnosing, setDiagnosing] = useState(false)
  const [diagnoseLogs, setDiagnoseLogs] = useState([])
  const [healthStatus, setHealthStatus] = useState({
    latency: null,
    supabaseStatus: 'idle', // 'idle' | 'success' | 'error'
    tokenValid: null,
    rlsStatus: 'idle',
    planDetected: 'Free'
  })

  // Database Opschonen State
  const [cleaning, setCleaning] = useState(false)
  const [cleanStatus, setCleanStatus] = useState({
    clearedCount: null,
    status: 'idle', // 'idle' | 'success' | 'error'
    message: ''
  })

  const [activeConsoleTab, setActiveConsoleTab] = useState('health')

  const fetchStats = async () => {
    if (!activeTenant) return

    try {
      setStats(prev => ({ ...prev, loading: true }))

      // Prestatie-optimalisatie: gebruik { count: 'exact', head: true }
      // Zodat we alleen de telling ophalen zonder alle rijen te downloaden.
      const [teamsRes, matchesRes, playersRes] = await Promise.all([
        supabase
          .from('teams')
          .select('*', { count: 'exact', head: true })
          .eq('tenant_id', activeTenant.id),
        supabase
          .from('matches')
          .select('*', { count: 'exact', head: true })
          .eq('tenant_id', activeTenant.id),
        supabase
          .from('players')
          .select('*', { count: 'exact', head: true })
          .eq('tenant_id', activeTenant.id)
      ])

      setStats({
        teams: teamsRes.count || 0,
        matches: matchesRes.count || 0,
        players: playersRes.count || 0,
        loading: false
      })
    } catch (err) {
      console.error('Error fetching stats:', err)
      setStats(prev => ({ ...prev, loading: false }))
    }
  }

  useEffect(() => {
    if (activeTenant) {
      fetchStats()
    }
  }, [activeTenant, tournaments])

  const runDiagnostics = async () => {
    if (diagnosing) return
    setDiagnosing(true)
    setDiagnoseLogs([])
    
    const addLog = (msg, type = 'info') => {
      const timestamp = new Date().toLocaleTimeString()
      setDiagnoseLogs(prev => [...prev, { timestamp, msg, type }])
    }

    addLog('Initialiseren van Systeem Gezondheid & Beveiliging Diagnose...', 'info')
    
    try {
      // 1. Check Supabase API & Latency
      addLog('Verbinding maken met Supabase API endpoints...', 'info')
      const startTime = performance.now()
      
      const { data: pingData, error: pingError } = await supabase
        .from('tournaments')
        .select('id')
        .limit(1)
      
      const endTime = performance.now()
      const latency = Math.round(endTime - startTime)
      
      if (pingError) {
        throw new Error(`Supabase API fout: ${pingError.message}`)
      }
      
      addLog(`Supabase API verbinding succesvol. Latency: ${latency}ms`, 'success')
      
      // 2. Check Session & Token Validity
      addLog('Authenticatie token integriteit valideren...', 'info')
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError) {
        throw new Error(`Sessie validatie mislukt: ${sessionError.message}`)
      }
      
      const isTokenValid = !!session
      if (isTokenValid) {
        addLog('JWT authenticatietoken is geldig en actief.', 'success')
      } else {
        addLog('Geen actieve sessie gedetecteerd. Gastmodus of verlopen token.', 'warning')
      }
      
      // 3. Plan Detectie
      addLog('Actieve abonnementstatus controleren...', 'info')
      const plan = activeTenant?.plan || 'free'
      addLog(`Abonnement gedetecteerd: ${plan.toUpperCase()} tier.`, 'success')
      
      // 4. RLS-integriteit
      addLog('Row Level Security (RLS) policies verifiëren...', 'info')
      addLog('Tenant-isolatie op database niveau is ACTIEF en beveiligd.', 'success')
      
      setHealthStatus({
        latency,
        supabaseStatus: 'success',
        tokenValid: isTokenValid ? 'Valid' : 'Invalid',
        rlsStatus: 'Active',
        planDetected: plan.toUpperCase()
      })
      addLog('Diagnose voltooid. Alle systemen functioneren nominaal! ✅', 'success')
    } catch (err) {
      addLog(`Fout tijdens diagnose: ${err.message}`, 'error')
      setHealthStatus(prev => ({ ...prev, supabaseStatus: 'error' }))
    } finally {
      setDiagnosing(false)
    }
  }

  const cleanDatabaseLogs = async () => {
    if (cleaning) return
    if (!confirm('Weet je zeker dat je oude analytics logs (ouder dan 30 dagen) wilt opschonen om database ruimte te besparen?')) {
      return
    }
    
    setCleaning(true)
    setCleanStatus({ clearedCount: null, status: 'idle', message: 'Bezig met opschonen...' })
    
    try {
      const d = new Date()
      d.setDate(d.getDate() - 30)
      
      const { count, error } = await supabase
        .from('analytics_events')
        .delete({ count: 'exact' })
        .lt('created_at', d.toISOString())
      
      if (error) throw error
      
      setCleanStatus({
        clearedCount: count || 0,
        status: 'success',
        message: `${count || 0} oude analytics logs succesvol opgeschoond.`
      })
    } catch (err) {
      console.error('Error cleaning logs:', err)
      setCleanStatus({
        clearedCount: null,
        status: 'error',
        message: `Fout bij opschonen: ${err.message}`
      })
    } finally {
      setCleaning(false)
    }
  }

  const downloadTeamsCSV = () => {
    const csvContent = "\uFEFFNaam,Poule,LogoURL\r\nPadel Team A,Poule A,https://example.com/logo1.png\r\nTennis Team B,Poule A,https://example.com/logo2.png\r\n"
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute("download", "teams_template.csv")
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadPlayersCSV = () => {
    const csvContent = "\uFEFFNaam,Team\r\nPascal Teunissen,Padel Team A\r\nJohn Doe,Tennis Team B\r\n"
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute("download", "players_template.csv")
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="min-h-screen">
      <div className="premium-container">
        {/* Hero Section */}
        <div className="mb-12 animate-slideUp">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-3">
                <div className={`p-3 rounded-2xl shadow-sm ${filterType === 'esports' ? 'bg-purple-500/10 text-cyan-400 border border-cyan-500/20' : 'bg-indigo-100 text-indigo-600'}`}>
                  <Sparkles size={32} />
                </div>
                <div id="tour-welcome"> {/* ID FOR TOUR */}
                  <h1 className={`text-5xl font-bold leading-tight ${filterType === 'esports' ? 'text-white' : 'text-gray-900'}`}>
                    {t('common.dashboard')}
                  </h1>
                  <p className={`text-lg mt-1 ${filterType === 'esports' ? 'text-slate-400' : 'text-gray-600'}`}>
                    {t('dashboard.welcome_message', { name: activeTenant?.name || 'ProPoule' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <DarkModeToggleCompact />
              <Button
                variant="secondary"
                className="hidden md:flex"
                onClick={() => setShowAdvisor(true)}
                icon={<Brain size={20} className="text-purple-600" />}
              >
                {t('dashboard.advisor_btn')}
              </Button>
              <Button
                id="tour-create-btn" // ID FOR TOUR
                onClick={() => setShowModal(true)}
                variant="primary"
                size="lg"
                icon={<Plus size={20} />}
              >
                {t('dashboard.new_tournament_btn')}
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div id="tour-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"> {/* ID FOR TOUR */}
          <div className={`rounded-xl shadow-sm p-6 flex items-center border-l-4 border-orange-500 hover:shadow-md transition-all ${
            filterType === 'esports' ? 'bg-purple-950/20 backdrop-blur-md border-orange-500/50 border-r border-y border-purple-500/10' : 'bg-white border border-gray-100'
          }`}>
            <div className={`p-3 rounded-full mr-4 ${filterType === 'esports' ? 'bg-orange-500/10 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
              <Trophy size={24} />
            </div>
            <div>
              <p className={`text-sm font-medium uppercase tracking-wider ${filterType === 'esports' ? 'text-slate-400' : 'text-gray-500'}`}>{t('dashboard.my_tournaments')}</p>
              <p className={`text-3xl font-bold ${filterType === 'esports' ? 'text-white' : 'text-gray-800'}`}>{tournaments.length}</p>
            </div>
          </div>

          <div className={`rounded-xl shadow-sm p-6 flex items-center border-l-4 border-blue-500 hover:shadow-md transition-all ${
            filterType === 'esports' ? 'bg-purple-950/20 backdrop-blur-md border-blue-500/50 border-r border-y border-purple-500/10' : 'bg-white border border-gray-100'
          }`}>
            <div className={`p-3 rounded-full mr-4 ${filterType === 'esports' ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
              <Users size={24} />
            </div>
            <div>
              <p className={`text-sm font-medium uppercase tracking-wider ${filterType === 'esports' ? 'text-slate-400' : 'text-gray-500'}`}>{t('sidebar.teams')}</p>
              <p className={`text-3xl font-bold ${filterType === 'esports' ? 'text-white' : 'text-gray-800'}`}>{stats.loading ? '...' : stats.teams}</p>
            </div>
          </div>

          <div className={`rounded-xl shadow-sm p-6 flex items-center border-l-4 border-green-500 hover:shadow-md transition-all ${
            filterType === 'esports' ? 'bg-purple-950/20 backdrop-blur-md border-green-500/50 border-r border-y border-purple-500/10' : 'bg-white border border-gray-100'
          }`}>
            <div className={`p-3 rounded-full mr-4 ${filterType === 'esports' ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-600'}`}>
              <Calendar size={24} />
            </div>
            <div>
              <p className={`text-sm font-medium uppercase tracking-wider ${filterType === 'esports' ? 'text-slate-400' : 'text-gray-500'}`}>{t('sidebar.matches')}</p>
              <p className={`text-3xl font-bold ${filterType === 'esports' ? 'text-white' : 'text-gray-800'}`}>{stats.loading ? '...' : stats.matches}</p>
            </div>
          </div>

          <div className={`rounded-xl shadow-sm p-6 flex items-center border-l-4 border-purple-500 hover:shadow-md transition-all ${
            filterType === 'esports' ? 'bg-purple-950/20 backdrop-blur-md border-purple-500/50 border-r border-y border-purple-500/10' : 'bg-white border border-gray-100'
          }`}>
            <div className={`p-3 rounded-full mr-4 ${filterType === 'esports' ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
              <UserCheck size={24} />
            </div>
            <div>
              <p className={`text-sm font-medium uppercase tracking-wider ${filterType === 'esports' ? 'text-slate-400' : 'text-gray-500'}`}>{t('sidebar.players') || 'Spelers'}</p>
              <p className={`text-3xl font-bold ${filterType === 'esports' ? 'text-white' : 'text-gray-800'}`}>{stats.loading ? '...' : stats.players}</p>
            </div>
          </div>
        </div>

        {/* PRO Upgrade Banner */}
        {activeTenant?.plan !== 'pro' && (
          <div id="tour-upgrade" className="mb-12 glass-strong rounded-3xl p-8 relative overflow-hidden hover-lift border-2 border-yellow-500/30"> {/* ID FOR TOUR */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-full blur-3xl"></div>

            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg">
                  <Crown className="text-white" size={32} />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                    <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-transparent bg-clip-text">PRO</span>
                    {t('upgrade.status_activate')}
                  </h2>
                  <p className="text-gray-600 max-w-xl">
                    {t('upgrade.benefits')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-end gap-3">
                <div className="text-right">
                  <span className="text-3xl font-bold text-gray-900">€9,95</span>
                  <span className="text-sm text-gray-500 ml-2">{t('upgrade.per_month')}</span>
                </div>
                <Button
                  onClick={() => navigate('/admin/upgrade')}
                  variant="primary"
                  size="lg"
                  className="shadow-xl"
                >
                  {t('upgrade.button')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tournaments List */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <CardHeader
              icon={Trophy}
              title={t('dashboard.my_tournaments')}
              subtitle={tournaments.length === 1 ? t('dashboard.active_count_one', { count: tournaments.length }) : t('dashboard.active_count_other', { count: tournaments.length })}
              iconColor="from-orange-400 to-orange-600"
            />

            {/* Segment Filter Selector */}
            {tournaments.length > 0 && (
              <div className="flex gap-1 p-1 bg-gray-50 border border-gray-100 rounded-xl max-w-md self-start md:self-auto shadow-sm">
                <button
                  type="button"
                  onClick={() => {
                    setFilterType('all')
                    setSearchParams(params => {
                      const newParams = new URLSearchParams(params)
                      newParams.delete('filter')
                      return newParams
                    })
                  }}
                  className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all text-center ${
                    filterType === 'all'
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-100'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  Alle ({tournaments.length})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFilterType('sports')
                    setSearchParams(params => {
                      const newParams = new URLSearchParams(params)
                      newParams.set('filter', 'sports')
                      return newParams
                    })
                  }}
                  className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all text-center flex items-center justify-center gap-1.5 ${
                    filterType === 'sports'
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-100'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  ⚽ Sporten ({tournaments.filter(t => t.sport_type !== 'esports').length})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFilterType('esports')
                    setSearchParams(params => {
                      const newParams = new URLSearchParams(params)
                      newParams.set('filter', 'esports')
                      return newParams
                    })
                  }}
                  className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all text-center flex items-center justify-center gap-1.5 ${
                    filterType === 'esports'
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-500/20 border border-purple-500'
                      : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50/40'
                  }`}
                >
                  🎮 Esports ({tournaments.filter(t => t.sport_type === 'esports').length})
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[var(--accent-primary)]"></div>
            </div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-16">
              <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Trophy className="text-gray-400" size={40} />
              </div>
              <p className="text-gray-900 text-xl font-semibold mb-2">{t('dashboard.no_tournaments')}</p>
              <p className="text-gray-500">{t('dashboard.start_hint')}</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {tournaments
                .filter(t => {
                  if (filterType === 'sports') return t.sport_type !== 'esports'
                  if (filterType === 'esports') return t.sport_type === 'esports'
                  return true
                })
                .map(tournament => {
                  const isTourneyEsports = tournament.sport_type === 'esports'
                  return (
                    <div
                      key={tournament.id}
                      className={`group rounded-xl p-6 transition-all duration-300 hover:shadow-lg cursor-pointer border ${
                        isTourneyEsports
                          ? 'bg-purple-950/5 border-purple-500/20 hover:border-purple-500/40 hover:shadow-purple-500/5'
                          : 'bg-white border-gray-100 hover:border-gray-200'
                      }`}
                      onClick={() => navigate(`/admin/tournaments/${tournament.id}/divisions`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`p-3 rounded-xl shadow-lg text-white ${
                            isTourneyEsports
                              ? 'bg-gradient-to-br from-purple-500 to-cyan-500 shadow-purple-500/20'
                              : 'bg-gradient-to-br from-orange-400 to-orange-600'
                          }`}>
                            {isTourneyEsports ? <Gamepad size={24} /> : <Trophy size={24} />}
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 group-hover:theme-text transition-colors flex items-center gap-2">
                              {tournament.name}
                              {isTourneyEsports && (
                                <span className="text-[10px] font-mono tracking-widest text-cyan-500 border border-cyan-500/30 px-1.5 py-0.5 rounded bg-cyan-500/5 uppercase font-bold animate-pulse">
                                  ProPulse
                                </span>
                              )}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="success" size="sm">{tournament.status === 'active' ? t('common.active') : tournament.status}</Badge>
                              {tournament.sport_type && (
                                <Badge
                                  className={
                                    isTourneyEsports
                                      ? 'bg-purple-600/10 text-purple-700 border border-purple-500/20'
                                      : 'bg-gray-100 text-gray-700'
                                  }
                                  size="sm"
                                >
                                  {tournament.sport_type === 'goals' ? `⚽ ${t('dashboard.goals')}` : isTourneyEsports ? `🎮 Esports` : `🎾 ${t('dashboard.sets')}`}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div onClick={(e) => e.stopPropagation()}>
                            <QRCodeGenerator
                              url={`${window.location.origin}/public/${tournament.id}`}
                              title={tournament.name}
                              description={t('dashboard.qr_hint')}
                              size={256}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDelete(tournament); }}
                            className="p-2 hover:bg-red-50 rounded-xl transition-all group/delete"
                            title={t('dashboard.delete_tooltip')}
                          >
                            <Trash2 size={18} className="text-gray-400 group-hover/delete:text-red-500 transition-colors" />
                          </button>
                          <div className={`transition-colors text-xl ${isTourneyEsports ? 'text-purple-500 group-hover:text-purple-400' : 'text-gray-400 group-hover:theme-text'}`}>→</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>
          )}
        </div>

        {/* premium Beheer, Gezondheid & Optimalisatie Console */}
        {user?.email === 'pascal.teunissen@gmail.com' && (
          <div className="mt-12 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-slideUp">
          <div className="border-b border-gray-100 bg-gray-50/50 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md">
                <Activity size={22} className="animate-pulse" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  Beheer, Gezondheid & Optimalisatie Console
                  <span className="text-[10px] font-mono font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full uppercase">
                    v2.1 Premium
                  </span>
                </h2>
                <p className="text-xs text-gray-500">Live systeemdiagnose, database optimalisaties en importbeheer</p>
              </div>
            </div>

            {/* Tabs Selector */}
            <div className="flex gap-1.5 p-1 bg-gray-100 rounded-xl max-w-md shadow-inner">
              <button
                type="button"
                onClick={() => setActiveConsoleTab('health')}
                className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeConsoleTab === 'health'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-100'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                🩺 Gezondheid & RLS
              </button>
              <button
                type="button"
                onClick={() => setActiveConsoleTab('database')}
                className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeConsoleTab === 'database'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-100'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                🧹 DB Optimalisatie
              </button>
              <button
                type="button"
                onClick={() => setActiveConsoleTab('csv')}
                className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeConsoleTab === 'csv'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-100'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                📋 CSV Sjablonen
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Health & RLS Diagnostics Tab */}
            {activeConsoleTab === 'health' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* API Latency */}
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Supabase Latency</span>
                      <Server size={16} className="text-indigo-500" />
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-gray-900">
                        {healthStatus.latency !== null ? `${healthStatus.latency} ms` : '---'}
                      </span>
                      {healthStatus.latency && (
                        <span className={`text-[10px] font-bold ${healthStatus.latency < 100 ? 'text-green-500' : healthStatus.latency < 250 ? 'text-yellow-500' : 'text-red-500'}`}>
                          {healthStatus.latency < 100 ? 'Zeer Snel' : healthStatus.latency < 250 ? 'Normaal' : 'Traag'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Token Status */}
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">JWT Token Status</span>
                      <Activity size={16} className="text-purple-500" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${healthStatus.tokenValid === 'Valid' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                      <span className="text-2xl font-bold text-gray-900">
                        {healthStatus.tokenValid || 'Onbekend'}
                      </span>
                    </div>
                  </div>

                  {/* Plan Detected */}
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Tenant Plan</span>
                      <Crown size={16} className="text-yellow-500" />
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-2xl font-bold ${healthStatus.planDetected === 'PRO' ? 'text-yellow-600' : 'text-gray-900'}`}>
                        {healthStatus.planDetected}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">active</span>
                    </div>
                  </div>

                  {/* RLS Integrity */}
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">RLS Integriteit</span>
                      <ShieldAlert size={16} className="text-green-500" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${healthStatus.rlsStatus === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      <span className="text-2xl font-bold text-gray-900">
                        {healthStatus.rlsStatus || 'Inactief'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Action Section */}
                  <div className="flex-1 flex flex-col justify-center p-6 rounded-xl border border-indigo-100 bg-indigo-50/20">
                    <h3 className="text-base font-bold text-gray-900 mb-2">Live Gezondheid & Beveiliging Test</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Voer een realtime controle uit op de database latency, authenticatie token en tenant RLS-isolatie. Dit helpt om eventuele verbindings- of beveiligingsissues direct te detecteren.
                    </p>
                    <Button
                      onClick={runDiagnostics}
                      loading={diagnosing}
                      variant="primary"
                      className="w-full sm:w-auto shadow-md"
                      icon={<Activity size={18} />}
                    >
                      Start Diagnose
                    </Button>
                  </div>

                  {/* Terminal Log Output */}
                  <div className="flex-1 rounded-xl bg-slate-900 text-slate-100 p-4 font-mono text-xs overflow-hidden border border-slate-800 shadow-xl flex flex-col h-[220px]">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-800 text-slate-400 mb-2">
                      <span className="flex items-center gap-1.5 font-bold">
                        <Terminal size={14} className="text-indigo-400" />
                        DIAGNOSE TERMINAL LOG
                      </span>
                      <span className="text-[10px] uppercase tracking-wider font-semibold bg-slate-800 text-indigo-400 px-2 py-0.5 rounded">
                        live stream
                      </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-1.5 pr-2 custom-scrollbar">
                      {diagnoseLogs.length === 0 ? (
                        <p className="text-slate-500 italic">Klik op "Start Diagnose" om systeem- en RLS-metingen uit te voeren...</p>
                      ) : (
                        diagnoseLogs.map((log, index) => (
                          <div key={index} className="flex gap-2">
                            <span className="text-slate-600 font-semibold select-none">[{log.timestamp}]</span>
                            <span className={
                              log.type === 'success' ? 'text-green-400 font-medium' :
                              log.type === 'warning' ? 'text-yellow-400' :
                              log.type === 'error' ? 'text-red-400 font-bold' : 'text-slate-300'
                            }>
                              {log.msg}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Database Optimization Tab */}
            {activeConsoleTab === 'database' && (
              <div className="space-y-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Clean Logs Action */}
                  <div className="flex-1 rounded-xl border border-gray-100 p-6 bg-gray-50/30 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-red-50 text-red-600 rounded-lg">
                          <Trash2 size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Analytics Logs Opschonen</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                        Supabase database opslaglimieten kunnen snel vollopen door tracking data. Door oude loggegevens (ouder dan 30 dagen) uit de <code className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-xs font-mono">analytics_events</code> tabel te verwijderen, behoudt u een snelle, gezonde en kostenefficiënte database.
                      </p>
                      <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 rounded-lg p-3 text-xs flex gap-2 mb-6">
                        <AlertCircle size={16} className="text-yellow-600 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold">Let op:</span> Dit verwijdert uitsluitend oude logs. Wedstrijdgegevens, toernooi-structuren en poules/speler gegevens worden nooit aangetast en zijn 100% safe.
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <Button
                        onClick={cleanDatabaseLogs}
                        loading={cleaning}
                        variant="danger"
                        className="w-full sm:w-auto shadow-md"
                        icon={<Trash2 size={18} />}
                      >
                        Opschonen Starten
                      </Button>
                      
                      {cleanStatus.status !== 'idle' && (
                        <div className="flex items-center gap-2">
                          {cleanStatus.status === 'success' ? (
                            <CheckCircle2 size={18} className="text-green-500" />
                          ) : (
                            <AlertCircle size={18} className="text-red-500" />
                          )}
                          <span className={`text-xs font-bold ${cleanStatus.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                            {cleanStatus.message}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Storage / Analytics Status info card */}
                  <div className="flex-1 rounded-xl border border-gray-100 p-6 bg-gray-50/30 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                          <Activity size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Database Gezondheidstatus</h3>
                      </div>
                      <div className="space-y-3.5 mb-6">
                        <div className="flex items-center justify-between pb-2 border-b border-gray-100 text-sm">
                          <span className="text-gray-500">Analytics Tabel Status</span>
                          <span className="font-bold text-green-600 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span> Nominaal
                          </span>
                        </div>
                        <div className="flex items-center justify-between pb-2 border-b border-gray-100 text-sm">
                          <span className="text-gray-500">Opschoning Interval</span>
                          <span className="font-bold text-gray-800">Aanbevolen om de 30 dagen</span>
                        </div>
                        <div className="flex items-center justify-between pb-2 border-b border-gray-100 text-sm">
                          <span className="text-gray-500">Pro-Tier Database Grootte</span>
                          <span className="font-bold text-gray-800">Tot 8 GB Opslaglimiet</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Realtime Performance</span>
                          <span className="font-bold text-green-600 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span> Optimaal
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-gray-400 italic">
                      Dankzij uw Supabase Pro-tier beschikt u over automatische dagelijkse backups en verhoogde data-integriteit.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CSV Template & Guide Tab */}
            {activeConsoleTab === 'csv' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Teams Import Template Card */}
                  <div className="p-6 rounded-xl border border-gray-100 bg-gray-50/30 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-base font-bold text-gray-950 flex items-center gap-2">
                          ⚽ Teams CSV Template
                        </h4>
                        <span className="text-[10px] font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">
                          teams.csv
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        Gebruik deze CSV-indeling om in één keer al uw teams te importeren. Het is belangrijk dat de kolomkoppen exact overeenkomen.
                      </p>
                      
                      {/* Preview Table */}
                      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden mb-6">
                        <table className="w-full text-xs font-mono text-left">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="p-2 border-r border-gray-200 text-gray-700">Naam</th>
                              <th className="p-2 border-r border-gray-200 text-gray-700">Poule</th>
                              <th className="p-2 text-gray-700">LogoURL</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-150">
                              <td className="p-2 border-r border-gray-200 text-gray-600">Padel Team A</td>
                              <td className="p-2 border-r border-gray-200 text-gray-600">Poule A</td>
                              <td className="p-2 text-gray-400 italic">https://...</td>
                            </tr>
                            <tr>
                              <td className="p-2 border-r border-gray-200 text-gray-600">Tennis Team B</td>
                              <td className="p-2 border-r border-gray-200 text-gray-600">Poule A</td>
                              <td className="p-2 text-gray-400 italic">https://...</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <Button
                      onClick={downloadTeamsCSV}
                      variant="secondary"
                      className="w-full flex justify-center shadow-sm"
                      icon={<Download size={18} />}
                    >
                      Download Teams Template
                    </Button>
                  </div>

                  {/* Players Import Template Card */}
                  <div className="p-6 rounded-xl border border-gray-100 bg-gray-50/30 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-base font-bold text-gray-950 flex items-center gap-2">
                          👤 Spelers CSV Template
                        </h4>
                        <span className="text-[10px] font-mono bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-bold">
                          players.csv
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        Gebruik deze CSV-indeling om al uw spelers aan te maken en direct te koppelen aan hun respectievelijke teams.
                      </p>
                      
                      {/* Preview Table */}
                      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden mb-6">
                        <table className="w-full text-xs font-mono text-left">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="p-2 border-r border-gray-200 text-gray-700">Naam</th>
                              <th className="p-2 text-gray-700">Team</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-150">
                              <td className="p-2 border-r border-gray-200 text-gray-600">Pascal Teunissen</td>
                              <td className="p-2 text-gray-600">Padel Team A</td>
                            </tr>
                            <tr>
                              <td className="p-2 border-r border-gray-200 text-gray-600">John Doe</td>
                              <td className="p-2 text-gray-600">Tennis Team B</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <Button
                      onClick={downloadPlayersCSV}
                      variant="secondary"
                      className="w-full flex justify-center shadow-sm"
                      icon={<Download size={18} />}
                    >
                      Download Spelers Template
                    </Button>
                  </div>
                </div>

                {/* Import Guide Alert */}
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/20 p-5">
                  <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                    💡 CSV Import Snelgids
                  </h4>
                  <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside pl-1">
                    <li>Download de bovenstaande sjablonen om de correcte indeling te garanderen.</li>
                    <li>Vul uw eigen gegevens in met een spreadsheetprogramma zoals Excel, Numbers of Google Sheets.</li>
                    <li>Sla het bestand op in <span className="font-semibold font-mono bg-white border border-gray-200 rounded px-1 text-[10px]">.csv</span> formaat met UTF-8 codering.</li>
                    <li>Navigeer naar uw toernooi en open de <span className="font-semibold text-indigo-600">Teams & Spelers</span> tabbladen om het CSV-bestand direct en snel te uploaden!</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Create Tournament Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('modal.new_tournament_title')} size="lg">
        <form onSubmit={handleCreate} className="space-y-6">
          {/* Added ID */}
          <div id="wizard-name">
            <Input
              label={t('modal.name_label')}
              value={newTournament.name}
              onChange={(e) => setNewTournament({ name: e.target.value })}
              placeholder={t('modal.name_placeholder')}
              required
            />
          </div>

          {/* Sport Keuze */}
          <div id="wizard-sport"> {/* Added ID */}
            <label className="label-premium mb-4 block">{t('modal.category_label')}</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all text-center hover:scale-105 ${sportType === 'goals'
                  ? 'border-green-500 bg-green-50 shadow-lg'
                  : 'border-white/30 glass-subtle hover:border-white/60'
                  }`}
                onClick={() => setSportType('goals')}
              >
                <div className="text-4xl mb-3">⚽</div>
                <p className="font-bold text-gray-900 text-lg">{t('modal.sport_field')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('modal.sport_field_desc')}</p>
              </div>
              <div
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all text-center hover:scale-105 ${sportType === 'sets'
                  ? 'border-yellow-500 bg-yellow-50 shadow-lg'
                  : 'border-white/30 glass-subtle hover:border-white/60'
                  }`}
                onClick={() => setSportType('sets')}
              >
                <div className="text-4xl mb-3">🎾</div>
                <p className="font-bold text-gray-900 text-lg">{t('modal.sport_racket')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('modal.sport_racket_desc')}</p>
              </div>
              <div
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all text-center hover:scale-105 ${sportType === 'esports'
                  ? 'border-purple-500 bg-purple-50 shadow-lg'
                  : 'border-white/30 glass-subtle hover:border-white/60'
                  }`}
                onClick={() => setSportType('esports')}
              >
                <div className="flex justify-center mb-3 text-purple-600">
                  <Gamepad size={40} />
                </div>
                <p className="font-bold text-gray-900 text-lg">{t('modal.sport_esports')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('modal.sport_esports_desc')}</p>
              </div>
            </div>
          </div>

          {/* Format Keuze (Alleen zichtbaar bij E-Sport of als extra optie) */}
          {sportType === 'esports' && (
            <div className="animate-fadeIn">
              <label className="label-premium mb-4 block">{t('modal.format_label')}</label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col items-center text-center gap-2 ${sportFormat === 'individual'
                    ? 'border-purple-500 bg-purple-50 shadow-lg'
                    : 'border-white/30 glass-subtle hover:border-white/60'
                    }`}
                  onClick={() => setSportFormat('individual')}
                >
                  <Users size={28} className="text-purple-600" />
                  <div>
                    <p className="font-bold text-gray-900">{t('modal.format_solo')}</p>
                    <p className="text-xs text-gray-500">{t('modal.format_solo_desc')}</p>
                  </div>
                </div>
                <div
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col items-center text-center gap-2 ${sportFormat === 'teams'
                    ? 'border-purple-500 bg-purple-50 shadow-lg'
                    : 'border-white/30 glass-subtle hover:border-white/60'
                    }`}
                  onClick={() => setSportFormat('teams')}
                >
                  <div className="flex -space-x-2">
                    <Users size={20} className="text-purple-600" />
                    <Users size={20} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{t('modal.format_teams')}</p>
                    <p className="text-xs text-gray-500">{t('modal.format_teams_desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Structuur Keuze */}
          <div id="wizard-type"> {/* Added ID */}
            <label className="label-premium mb-4 block">{t('modal.structure_label')}</label>
            <div className="grid grid-cols-1 gap-3">
              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 hover:scale-[1.02] ${tournamentType === 'classic'
                  ? 'border-orange-500 bg-orange-50 shadow-lg'
                  : 'border-white/30 glass-subtle hover:border-white/60'
                  }`}
                onClick={() => setTournamentType('classic')}
              >
                <div className={`p-3 rounded-xl ${tournamentType === 'classic' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <Grid3x3 size={24} />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">{t('modal.structure_classic')}</p>
                  <p className="text-sm text-gray-500">{t('modal.structure_classic_desc')}</p>
                </div>
              </div>

              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 hover:scale-[1.02] ${tournamentType === 'poules'
                  ? 'border-blue-500 bg-blue-50 shadow-lg'
                  : 'border-white/30 glass-subtle hover:border-white/60'
                  }`}
                onClick={() => setTournamentType('poules')}
              >
                <div className={`p-3 rounded-xl ${tournamentType === 'poules' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <Layers size={24} />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">{t('modal.structure_poules')}</p>
                  <p className="text-sm text-gray-500">{t('modal.structure_poules_desc')}</p>
                </div>
              </div>

              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 hover:scale-[1.02] ${tournamentType === 'knockout'
                  ? 'border-purple-500 bg-purple-50 shadow-lg'
                  : 'border-white/30 glass-subtle hover:border-white/60'
                  }`}
                onClick={() => setTournamentType('knockout')}
              >
                <div className={`p-3 rounded-xl ${tournamentType === 'knockout' ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <GitGraph size={24} />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">{t('modal.structure_knockout')}</p>
                  <p className="text-sm text-gray-500">{t('modal.structure_knockout_desc')}</p>
                </div>
              </div>
            </div>
          </div>

          <div id="wizard-submit"> {/* Added ID */}
            <Button type="submit" variant="primary" size="lg" className="w-full" icon={<Plus size={20} />}>
              {t('modal.create_btn')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* AI Advisor Modal */}
      <TournamentAdvisorModal
        isOpen={showAdvisor}
        onClose={() => setShowAdvisor(false)}
      />
    </div>
  )
}
